import React, { useEffect, useState, useContext, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useForm, FormProvider as RHFProvider } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { FormContext } from '../contexts/formContexts';
import applicationService from '../services/ApplicationService';
import ApplicationContext from '../contexts/applicationContext';
import AdditionalVehicleForm from '../components/MultiStepForm/AdditionalVehicleForm';
import { applicationFields } from '../config/applicationConfig';
import { formatDate, formatSSN } from '../utils/formatData';
import FormField from '../components/FormField';
import ProgressBar from '../components/MultiStepForm/ProgressBar';
import StepNavigator from '../components/MultiStepForm/StepNavigator';
import { collectFormData } from '../utils/collectFormDate';
import { CREDIT_UNION } from '../assets/constants';
import { decodeVin, extractVehicleInfo } from '../services/vehicleService';
import { Tooltip } from 'bootstrap';
import populateData from '../utils/populateData';
import { saveToLocalStorage, loadFromLocalStorage } from '../utils/formStatePersistence';
import './ApplicationForm.css';

import { buildYupSchema } from '../utils/validationSchemas';
import { stepOneSchema } from '../utils/Validations/stepOneValidation';
import { stepTwoSchema } from '../utils/Validations/stepTwoValidation';
import { stepThreeSchema } from '../utils/Validations/stepThreeValidation';
import { stepFourSchema } from '../utils/Validations/stepFourValidation';
import { stepFiveSchema } from '../utils/Validations/stepFiveValidation';


const ApplicationForm = () => {
  const { id: paramId } = useParams();
  const query = new URLSearchParams(useLocation().search);
  const queryId = query.get('id');
  const id = paramId || queryId;

  const formRef = useRef();
  const [currentId, setCurrentId] = useState('');
  const { formData, updateFormData, currentStep, setCurrentStep } = useContext(FormContext);
  const [additionalVehicles, setAdditionalVehicles] = useState([]);
  const [isLoading, setIsLoading] = useState(true); // Track loading state
  const [submissionError, setSubmissionError] = useState();
  const navigate = useNavigate();

  const step = applicationFields[currentStep];
   const schema =
  currentStep === 0
    ? stepOneSchema
    : currentStep === 1
    ? stepTwoSchema
    : currentStep === 2
    ? stepThreeSchema
    : currentStep === 3
    ? stepFourSchema
    : currentStep === 4
    ? stepFiveSchema
    : buildYupSchema(step.fields, currentStep);
  const persistedValues = loadFromLocalStorage();
  const hasInitialized = useRef(false);
  const methods = useForm({
    resolver: yupResolver(schema),
    defaultValues: populateData({
      coapplicant: 'No',
      co_borrower_dob: null,
      co_borrower_employer_since: null,
      co_borrower_drivers_license_issue_date: null,
      co_borrower_drivers_license_expire_date: null,
      co_borrower_address_since: null,
      social_security_number: null,
      social_security_number:'',
      ...formData,
    }),
    mode: 'all',
    reValidateMode: 'onChange',
    shouldUnregister: false, //  critical for conditional rendering like co-borrower
  });

  const { watch, reset, trigger, clearErrors, setValue,getValues, formState: { errors } } = methods;
  const formValues = useRef({});
  useEffect(() => {
    const subscription = watch((value) => {
      formValues.current = value;
    });
    return () => subscription.unsubscribe();
  }, [watch]);
  

useEffect(() => {
  const initializeApplication = async () => {
    if (hasInitialized.current) return;
    hasInitialized.current = true;

    setIsLoading(true);
    try {
      let guid;
      if (id) {
        console.log('Found ID in URL:', id);
        ApplicationContext.setId(id);
        setCurrentId(id);

        const rawData = await applicationService.getForm(id);
        console.log(' Raw data from backend:', rawData);

        if (rawData) {
          const resumeKey = `${rawData.last_name?.toLowerCase()}_${rawData.dob}_${rawData.zip}`;
          if (resumeKey) localStorage.setItem(resumeKey, id);
        
          const populatedData = populateData(rawData);
          updateFormData(populatedData);
          setAdditionalVehicles(populatedData.additional_vehicles || []);
        
          reset(populatedData); 
        }
        else {
          console.error('No data returned for this ID');
        }
      } else {
        const savedGuid = sessionStorage.getItem('current_guid');
        if (savedGuid) {
          guid = savedGuid;
        } else {
          const response = await applicationService.generateGuid();
          guid = response.guid;
          sessionStorage.setItem('current_guid', guid); //  store it per-tab
        }
      
        ApplicationContext.setId(guid);
        setCurrentId(guid);
        updateFormData({ form_type: 'refi' });

        const resumeKey = `${formData.last_name?.toLowerCase()}_${formData.dob}_${formData.zip}`;
        if (resumeKey && !localStorage.getItem(resumeKey)) {
          localStorage.setItem(resumeKey, guid);
          console.log(' Stored resumeKey:', resumeKey, '->', guid);
        }
      }
    } catch (error) {
      console.error('Error initializing application:', error);
    } finally {
      setIsLoading(false);
    }
  };

  initializeApplication();
}, [id, updateFormData]); // Add updateFormData to dependencies 
 


useEffect(() => {
  if (!id && !formData?.guid) {
    const saved = loadFromLocalStorage();
    if (saved && Object.keys(saved).length > 0) {
      updateFormData(saved);
      reset(saved); // restores form state
      if (saved.additional_vehicles) {
        setAdditionalVehicles(saved.additional_vehicles);
      }
    }
  }
}, []);


useEffect(() => {
  if (isLoading || !hasInitialized.current) return;

  const defaultValues = populateData(formData);

  step.fields.forEach(field => {
    if (field.type === 'date' && defaultValues[field.name] && defaultValues[field.name] !== null) {
      defaultValues[field.name] = formatDate(defaultValues[field.name]);
    }
    if (field.formatPattern === '###-##-####' && defaultValues[field.name]) {
      defaultValues[field.name] = formatSSN(defaultValues[field.name]);
    }
    if (field.conditional?.fields?.length) {
      field.conditional.fields.forEach(sub => {
        if (sub.type === 'date' && defaultValues[sub.name] && defaultValues[sub.name] !== null) {
          defaultValues[sub.name] = formatDate(defaultValues[sub.name]);
        }
        if (sub.formatPattern === '###-##-####' && defaultValues[sub.name]) {
          defaultValues[sub.name] = formatSSN(defaultValues[sub.name]);
        }
      });
    }
  });

  // console.log('Resetting with defaultValues:', defaultValues);
  reset(defaultValues, { keepErrors: false, keepValues: true });
  clearErrors();
}, [currentStep, step.fields, isLoading]);

useEffect(() => {
  const subscription = watch((value, { name }) => {
    if (name === 'vin' && value.vin?.length === 17) {
      decodeVin(value.vin).then((results) => {
        const vehicle = extractVehicleInfo(results);
        if (vehicle.make) setValue('make', vehicle.make);
        if (vehicle.model) setValue('model', vehicle.model);
        if (vehicle.model_year) setValue('model_year', vehicle.model_year);
      });
    }
  });

  return () => subscription.unsubscribe();
}, [watch, setValue]);


useEffect(() => {
  const subscription = watch((value, { name }) => {
    if (name === 'employment_status' && value.employment_status === 'un-employed') {
      setValue('employer_since', null, { shouldValidate: true });
      setValue('employer_phone_1', null, { shouldValidate: true });
      setValue('employer_zip', null, { shouldValidate: true });
    }
    
  });
  return () => subscription.unsubscribe();
}, [watch, setValue]);


//address since - fields pop up if less than 2 years
useEffect(() => {
  const addressSince = watch('address_since');
  if (!addressSince) return;

  const selected = new Date(addressSince);
  const twoYearsAgo = new Date();
  twoYearsAgo.setFullYear(twoYearsAgo.getFullYear() - 2);

  const showPast = selected > twoYearsAgo;
  setValue('show_past_address', showPast);
}, [watch('address_since'), setValue]);

//employee since - fields pop up if less than 2 years
useEffect(() => {
  const since = watch('employer_since');
  if (!since) return;

  const selected = new Date(since);
  const twoYearsAgo = new Date();
  twoYearsAgo.setFullYear(twoYearsAgo.getFullYear() - 2);

  setValue('show_past_employment', selected > twoYearsAgo);
}, [watch('employer_since'), setValue]);

//co-borrower employee since - fields pop up if less than 2 years
useEffect(() => {
  const since = watch('co_borrower_employer_since');
  if (!since) return;

  const selected = new Date(since);
  const twoYearsAgo = new Date();
  twoYearsAgo.setFullYear(twoYearsAgo.getFullYear() - 2);

  setValue('co_borrower_show_past_employment', selected > twoYearsAgo);
}, [watch('co_borrower_employer_since'), setValue]);

//co-borrower address since - fields pop up if less than 2 years
useEffect(() => {
  const since = watch('co_borrower_address_since');
  if (!since) return;

  const selected = new Date(since);
  const twoYearsAgo = new Date();
  twoYearsAgo.setFullYear(twoYearsAgo.getFullYear() - 2);

  setValue('co_borrower_show_past_address', selected > twoYearsAgo);
}, [watch('co_borrower_address_since'), setValue]);

useEffect(() => {
  const coapplicantValue = watch('coapplicant');

  if (coapplicantValue === 'No') {
    const coBorrowerFields = Object.keys(getValues()).filter((key) =>
      key.startsWith('co_borrower_')
    );

    coBorrowerFields.forEach((fieldName) => {
      setValue(fieldName, undefined, { shouldValidate: false });
    });

    // Force clear any validation errors
    clearErrors(coBorrowerFields);
  }
}, [watch('coapplicant')]);


useEffect(() => {
  const subscription = watch((value) => {
    const fullData = collectFormData(formData, value);
    saveToLocalStorage(fullData); // Save to localStorage on every change
  });
  return () => subscription.unsubscribe();
}, [watch, formData]);

const scrollToFormTop = () => {
  if (formRef.current) {
    const y = formRef.current.getBoundingClientRect().top + window.pageYOffset - 100;
    window.scrollTo({ top: y, behavior: 'smooth' });
  }
};

useEffect(() => {
  scrollToFormTop();
}, [currentStep]);


  if (!currentId) {
    return <div className="text-center mt-5">Loading application...</div>;
  }

  //  Handle form submission
  const onSubmit = async (data) => {
    setValue('credit_check_consent', 'Submit my Application!', { shouldValidate: false });

    // Then get the updated values and submit
    const updatedData = getValues(); // from useFormContext()
  
    const collectFieldNames = (fields) => {
      let names = [];
      for (const field of fields) {
        if (field.name) names.push(field.name);
    
        if (field.conditional?.fields) {
          names.push(...collectFieldNames(field.conditional.fields));
        }
    
        if (field.fields) {
          names.push(...collectFieldNames(field.fields));
        }
      }
      return names;
    };
    
    const currentStepFields = collectFieldNames(step.fields);
    
      const isValid = await trigger(currentStepFields, { shouldFocus: true });

      if (!isValid) {
        const firstErrorField = currentStepFields.find((name) => !!methods.getFieldState(name).error);
        if (firstErrorField) methods.setFocus(firstErrorField);
        const errorElement = document.querySelector(`[name="${firstErrorField}"]`);
        if (!isValid) {
          const firstErrorField = currentStepFields.find((name) => !!methods.getFieldState(name).error);
        
          if (firstErrorField) {
            try {
              await applicationService.saveFormStep(updatedData);
              methods.setFocus(firstErrorField);
              const errorElement = document.querySelector(`[name="${firstErrorField}"]`);
              if (errorElement && typeof errorElement.scrollIntoView === 'function') {
                errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
              }
            } catch (err) {
              console.warn('Failed to scroll to error field:', err);
            }
          }
          return;
        }
        
        return;
      }

      

    try {
      // Object.entries(data).forEach(([key, val]) => updateFormData(key, val));
      // const fullPayload = collectFormData(formData, data);
      const sanitizePayload = (obj) =>  Object.fromEntries(Object.entries(obj).filter(([k, v]) => k && k !== 'undefined' && v !== undefined));
      const fullPayload = sanitizePayload(collectFormData(formData, data));
      
      if (!fullPayload.guid) {
        fullPayload.guid = currentId;
      }      
      if (fullPayload.last_name) {
        fullPayload.last_name = fullPayload.last_name.toLowerCase();
      }
      fullPayload.form_type = 'refi';
      fullPayload.guid = currentId;

      // Null sanitization
      for (const key in fullPayload) {
        if (
          fullPayload[key] === '' ||
          fullPayload[key] === 'null' ||
          fullPayload[key] === null
        ) {
          delete fullPayload[key]; // Remove it completely instead of setting to null
        }
      }
      

      // Date formatting
      const dateFields = [
        'dob', 'co_borrower_dob', 'employer_since', 'co_borrower_employer_since',
        'address_since', 'drivers_license_issue_date', 'drivers_license_expire_date',
        'co_borrower_drivers_license_issue_date', 'co_borrower_drivers_license_expire_date',
        'co_borrower_address_since', 'next_payment_date', 'next_payment_date_2',
      ];
      dateFields.forEach((field) => {
        if (fullPayload[field]) {
          fullPayload[field] = formatDate(fullPayload[field]);
        }
      });

      //  Recaptcha validation and submit or save
      if (window.grecaptcha && window.grecaptcha.enterprise) {
        await window.grecaptcha.enterprise.ready(async () => {
          const recaptchaToken = await window.grecaptcha.enterprise.execute(
            '6LeImWIqAAAAAMw3SQD0NbX2Q7_8cZzYukSMMxxH',
            { action: currentStep === applicationFields.length - 1 ? 'SUBMIT' : 'SAVE_FORM' }
          );
          fullPayload.g_recaptcha_response = recaptchaToken;

          if (currentStep < applicationFields.length - 1) {
            // console.log('Payload being sent to backend:', fullPayload);
            await applicationService.saveFormStep(currentStep + 1, fullPayload);
            setCurrentStep(currentStep + 1);
            setTimeout(() => {
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }, 0);

          } else {
            fullPayload.credit_consent = true;
            fullPayload.lang = localStorage.getItem('lang') || 'en'; // or however you're tracking language
            localStorage.setItem('current_guid', currentId);
            const currencyFields = [
              "monthly_income",
              "monthly_home_payment",
              "other_expenses",
              "current_monthly_payment",
              "current_payoff",
              "co_borrower_monthly_income",
              "co_borrower_monthly_home_payment",
              "co_borrower_other_expenses",
              "co_borrower_current_monthly_payment",
              "co_borrower_current_payoff"
            ];
            
            currencyFields.forEach(field => {
              if (fullPayload[field]) {
                fullPayload[field] = String(fullPayload[field]).replace(/[$,]/g, '');
              }
            });
            
            await applicationService.submitFinalForm(fullPayload, currentId);
            reset(); // Reset form after successful submission
            sessionStorage.removeItem('current_guid'); // Clear session storage
            const resumeKey = `${fullPayload.last_name?.toLowerCase()}_${fullPayload.dob}_${fullPayload.zip}`;
            localStorage.removeItem(resumeKey); // Clear resume key

            ApplicationContext.setId(''); // Clear context ID
            setCurrentId(''); // Clear current ID
            setAdditionalVehicles([]); // Clear additional vehicles
            updateFormData({}); // Clear form data in context
            setCurrentStep(0); // Reset step to 0
            navigate('/thank-you');
          }
        });
      } else {
        throw new Error('Recaptcha not initialized. Please refresh or check network.');
      }
    } catch (err) {
      setSubmissionError(
        err.response?.data?.message || 'Something went wrong. Please try again.'
      );
    }
  };

  //  Dynamic field rendering logic
  const renderFields = (fields = step.fields) => {
  const watchedValues = watch();
    const rows = [];
    let currentRow = [];
  
    fields.forEach((field, index) => {
      //  Handle Section Headers (type: 'heading')
      if (field.type === 'heading') {
        if (currentRow.length > 0) {
          rows.push(<div className="row" key={`row-${index - 1}`}>{currentRow}</div>);
          currentRow = [];
        }
  
        rows.push(
          <div className="row" key={`heading-${index}`}>
            <div className="col-12 mb-2">
              <h5 className="form-section-title mt-4">{field.label}</h5>
              {/* <hr className="mb-3 mt-2" /> */}
            </div>
          </div>
        );
        return;
      }
  
      //  Handle Conditional Sections
      if (field.conditional?.fields?.length) {
        const conditionValue = watchedValues[field.conditional.field];
        const showCondition = field.conditional.values.includes(conditionValue);
  
        currentRow.push(<FormField key={field.name} {...field}  validationPattern={field.validationPattern} validationMessage={field.validationMessage} />);
        if (showCondition) {
          currentRow.push(
            <React.Fragment key={`conditional-${field.name}`}>
              {renderFields(field.conditional.fields)}
            </React.Fragment>
          );
        }
      }
  
      //  Handle Grouped Fields
      else if (field.fields && Array.isArray(field.fields)) {
        currentRow.push(
          <div className="col-12" key={`group-${field.subtitle || field.name}`}>
            {field.subtitle && (
              <>
                <h6 className="form-subheading mt-4 mb-2">{field.subtitle}</h6>
              </>
            )}
            {renderFields(field.fields)}
          </div>
        );
        
      }
      
  
      //  Inject Additional Vehicle Form before "comments"
      else if (field.name) {
        if (currentStep === 4 && field.name === 'comments') {
          currentRow.push(
            <div className="col-12 mb-3" key={`add-vehicles`}>
              {additionalVehicles.map((_, index) => (
                <AdditionalVehicleForm
                  key={index}
                  index={index}
                  onRemove={(idxToRemove) =>
                    setAdditionalVehicles((prev) => prev.filter((_, i) => i !== idxToRemove))
                  }
                />
              ))}
              {additionalVehicles.length < 2 && (
                <div className="text-center">
                  <button
                    type="button"
                    className="btn btn-outline-primary mb-3 mt-2 px-4 py-2 fw-semibold"
                    onClick={() => setAdditionalVehicles((prev) => [...prev, {}])}
                  >
                     + Add Vehicle 
                  </button>
                </div>
              )}
            </div>
          );
        }
  
        currentRow.push(<FormField key={field.name} {...field}  validationPattern={field.validationPattern} validationMessage={field.validationMessage}/>);
      }
  
      //  Handle Row Breaks
      if (field.breakAfter || index === fields.length - 1) {
        rows.push(<div className="row" key={`row-${index}`}>{currentRow}</div>);
        currentRow = [];
      }
    });
  
    return rows;
  };
  

  
  return (
    <>
    <div className="application">
      <div className="banner fixed-top">
        <small>
          Still have a question? Reach out to us at <u>{CREDIT_UNION.INFO.PHONE}</u> or{' '}
          <u>{CREDIT_UNION.INFO.EMAIL}</u>
        </small>
      </div>

      <div className="content container mt-5 mb-5" ref={formRef}>
        <RHFProvider {...methods}>
          <div className="form-box p-4 shadow-sm rounded bg-white">
            <div className="row">
              <div className="col-md-3 pe-4">
                <ProgressBar steps={applicationFields} currentStep={currentStep} />
              </div>
              <div className="col-md-9 ps-4">
                <h4 className="form-subtitle mb-2">
                {typeof step.subtitle === 'function' ? step.subtitle(watch() || {}) : step.subtitle}
                </h4>
                
                <h6 className="form-subtext mb-4">{step.text}</h6>
                
                <form onSubmit={methods.handleSubmit(onSubmit)}>
                  {renderFields()}
                  {/* <hr className="mt-4 mb-3" /> */}
                  {['co_borrower_monthly_home_payment','monthly_income', 'monthly_home_payment', 'other_expenses'].map((name) => (
                    <input key={name} type="text" style={{ display: 'none' }} {...methods.register(name)} />
                  ))}

                  {submissionError && (
                    <div className="alert alert-danger" role="alert">
                      {submissionError}
                    </div>
                  )}

                  <StepNavigator formRef={formRef} />
                </form>
                

              </div>
            </div>
          </div>
        </RHFProvider>

      </div>
  

    </div>
    
    {/* <Footer /> */}
    </>
  );

};

export default ApplicationForm;
